/**
 * JoinKindDropdown — replaces JoinLine's disabled kind badge with a real
 * shadcn DropdownMenu when an editable VqeEditContext is supplied. Selecting
 * a kind commits a `modify-join-kind` StructuralEdit via `changeJoinKind`,
 * preserving user casing and (per SR-Edit's planner) dropping the ON
 * predicate when transitioning to a CROSS-style kind.
 *
 * Dialect-incompatible kinds are filtered out: CROSS APPLY / OUTER APPLY are
 * MSSQL-only (Oracle 12c+ also supports them but v1 keeps the gate
 * conservative); LATERAL is rendered via the JoinClause.isLateral flag and
 * is not selectable as a top-level kind here.
 *
 * PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — change JOIN kind.
 */

import { ChevronDown } from 'lucide-react';

import { cn } from '../../../../../lib/utils';
import { badgeVariants } from '../../../../../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../../../components/ui/dropdown-menu';
import type { ParseOptions } from '../../../../sql-grammar/model';
import type { VqeEditContext } from '../../edit/editContext';
import { toCommitCtx } from '../../edit/editContext';
import { changeJoinKind } from '../../edit/edits/changeJoinKind';
import { VqeTooltip } from '../common/VqeTooltip';
import type { JoinKind as RenderJoinKind } from './extractFromJoins';
import {
  KIND_ARIA,
  KIND_BADGE_CLASS,
  KIND_LABELS,
  KIND_TOOLTIPS,
  availableKindsForEngine,
  renderKindToSrEditKind,
} from './joinKindMaps';

export interface JoinKindDropdownProps {
  readonly currentKind: RenderJoinKind;
  readonly isLateral: boolean;
  readonly joinIndex: number;
  readonly editContext: VqeEditContext;
  readonly options: ParseOptions;
  readonly testIdPrefix: string;
}

export function JoinKindDropdown({
  currentKind,
  isLateral,
  joinIndex,
  editContext,
  options,
  testIdPrefix,
}: JoinKindDropdownProps) {
  const kindLabel = KIND_LABELS[currentKind];
  const choices = availableKindsForEngine(options.engine);
  const onSelect = (next: RenderJoinKind): void => {
    if (next === currentKind) return;
    void changeJoinKind(joinIndex, renderKindToSrEditKind(next), toCommitCtx(editContext));
  };
  return (
    <DropdownMenu>
      <VqeTooltip content={KIND_TOOLTIPS[currentKind]}>
        <DropdownMenuTrigger
          type="button"
          aria-label={`${KIND_ARIA[currentKind]} kind dropdown`}
          data-testid={`${testIdPrefix}-kind-dropdown`}
          className={cn(
            badgeVariants({ variant: 'secondary' }),
            'gap-1 cursor-pointer hover:opacity-90',
            KIND_BADGE_CLASS[currentKind],
          )}
        >
          <span data-testid={`${testIdPrefix}-kind-label`}>{kindLabel}</span>
          {isLateral ? (
            <span
              className="ml-1 rounded bg-teal-500 px-1 py-0 text-[10px] font-semibold uppercase tracking-wide text-white"
              data-testid={`${testIdPrefix}-lateral`}
            >
              LATERAL
            </span>
          ) : null}
          <ChevronDown className="h-3 w-3" aria-hidden />
        </DropdownMenuTrigger>
      </VqeTooltip>
      <DropdownMenuContent align="start" data-testid={`${testIdPrefix}-kind-menu`}>
        {choices.map((choice) => (
          <DropdownMenuItem
            key={choice}
            data-testid={`${testIdPrefix}-kind-option-${choice.toLowerCase()}`}
            onSelect={() => onSelect(choice)}
            className={choice === currentKind ? 'font-semibold' : undefined}
          >
            <div className="flex flex-col gap-0.5">
              <span>{KIND_LABELS[choice]}</span>
              <span className="text-[10px] font-normal normal-case text-muted-foreground">
                {KIND_TOOLTIPS[choice]}
              </span>
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
